/**
 * Anchor Link Browser Transport v3.6.4
 * https://github.com/greymass/anchor-link-browser-transport
 *
 * @license
 * Copyright (c) 2020 Greymass Inc. All Rights Reserved.
 * 
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 * 
 *  1. Redistribution of source code must retain the above copyright notice, this
 *     list of conditions and the following disclaimer.
 * 
 *  2. Redistribution in binary form must reproduce the above copyright notice,
 *     this list of conditions and the following disclaimer in the documentation
 *     and/or other materials provided with the distribution.
 * 
 *  3. Neither the name of the copyright holder nor the names of its contributors
 *     may be used to endorse or promote products derived from this software without
 *     specific prior written permission.
 * 
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * 
 * YOU ACKNOWLEDGE THAT THIS SOFTWARE IS NOT DESIGNED, LICENSED OR INTENDED FOR USE
 * IN THE DESIGN, CONSTRUCTION, OPERATION OR MAINTENANCE OF ANY MILITARY FACILITY.
 */
import { Signature, SigningRequest, PlaceholderName, Link, Base64u, isInstanceOf, APIError } from '@amax/anchor-link';

var styleText = `
/* Anchor Link */
:root {
    color-scheme: no-preference;
    background: white;
    color: black;
}
.%prefix% * {
    box-sizing: border-box;
    line-height: 1;
    font-variant-numeric: tabular-nums;
}

.%prefix% {
    font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue',
        Arial, sans-serif;
    font-size: 13px;
    background: rgba(0, 0, 0, 0.65);
    position: fixed;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    z-index: 2147483647;
    display: none;
    align-items: center;
    justify-content: center;
}

.%prefix%-active {
    display: flex;
}

.%prefix%-inner {
    background: #FFF;
    margin: 20px;
    padding: 24px 0;
    border-radius: 20px;
    box-shadow: 0px 4px 100px rgba(0, 0, 0, .5);
    width: 484px;
    position: relative;
}

.%prefix%-error{
    color: #fc3d39;
    margin-top: 14px;
    line-height: 24px;
}

.%prefix%-close {
    display: block;
    position: absolute;
    top: 28px;
    right: 28px;
    width: 28px;
    height: 28px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M.57 12.1a.96.96 0 000 1.34c.37.36 1 .36 1.34 0L7 8.37l5.09 5.09c.36.35.97.35 1.34-.01a.96.96 0 000-1.34L8.34 7.01l5.08-5.08a.95.95 0 000-1.33.97.97 0 00-1.34-.01L6.99 5.68 1.91.59a.96.96 0 00-1.33 0 .97.97 0 00-.01 1.34l5.09 5.08-5.1 5.1z' fill='%238486A4'/%3E%3C/svg%3E");
    background-size: 14px;
    background-repeat: no-repeat;
    background-position: 50% 7px;
    border-radius: 100%;
    cursor: pointer;
}

.%prefix%-close:hover {
    background-color: white;
}

.%prefix%-version {
    position: absolute;
    top: 19px;
    left: 20px;
    cursor: help;
    color: #B8C0DA;
    opacity: 0.1;
    display: none;
}

.%prefix%-version:hover {
    opacity: 1;
}

.%prefix%-logo {
    width: 70px;
    height: 70px;
    color: #536EF3;
    margin: 0 auto;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 70 70'%3E%3ClinearGradient id='a' gradientUnits='userSpaceOnUse' x1='62.388' y1='4.055' x2='9.311' y2='67.901' gradientTransform='matrix(1 0 0 -1 0 72)'%3E%3Cstop offset='.213' stop-color='%23536ef3'/%3E%3Cstop offset='.995' stop-color='%235d77f7'/%3E%3C/linearGradient%3E%3Cpath d='M35 0c19.3 0 35 15.7 35 35S54.3 70 35 70 0 54.3 0 35 15.7 0 35 0z' fill='url(%23a)'/%3E%3Cpath d='M59.9 49l-22-37.3c-.1-.1-.3-.3-.6-.3h-4.4c-.3 0-.5.2-.6.3L10.1 49c-.1.2-.1.3-.1.4 0 .1 0 .3.1.4l2.2 3.8.2.2h9.2c.3 0 .5-.2.6-.3l2.2-3.8c.1-.1.1-.3.1-.4 0-.1 0-.3-.1-.4l-2-3.4 3.4-5.7 8.2 13.7.1.1.1.1c.1.1.2.1.3.1H57c.3 0 .5-.2.6-.3l2.2-3.7c.1-.1.1-.3.1-.4.1-.1.1-.2 0-.4zm-16.1-9.2l1.2 2.1-2.1 3.5-2.3 3.8v.6l.1.1c.1.1.3.3.6.3h16.4l-1.2 2.1H36.4l7.4-12.5zm-24.9 9.3c-.1.1-.1.2-.1.4s.1.3.1.4l.1.1c.1.1.3.3.6.3h3l-1.3 2.2h-6.9l18.4-31 1.2 2.1-4.1 6.9c-.1.1-.1.3-.1.4 0 .1 0 .3.1.4l6.4 10.8-1.3 2.2-5.9-10.1c-.1-.1-.3-.3-.6-.3s-.5.2-.6.3l-6.7 11-2.3 3.9zm12.6-18.3l3.4-5.8 5.7 9.5-3.5 5.9-5.6-9.6zm-3.2 5.5l5.9 10c.1.2.3.3.5.4.2.1.4.1.5 0 .1 0 .2-.1.3-.2.1-.1.1-.2.2-.3l6.6-11.3c.1-.1.1-.3.1-.4 0-.1 0-.3-.1-.4l-8.8-14.7s-.1-.2-.2-.3c-.1-.1-.3-.2-.5-.2-.3 0-.4.1-.5.2-.1.1-.2.2-.2.3L13 51.6l-1.3-2.2 21.1-35.7 18.4 31.1h-2.5l-4.2-7.1c-.2-.2-.4-.4-.7-.4-.3 0-.5.1-.7.4L35 51.6l-7.9-13.2 1.2-2.1zM46 43.4l.8 1.4h-1.7l.9-1.4zm-1.9 3h8.5c.3 0 .5-.2.6-.3.1-.1.2-.3.2-.5 0-.1 0-.3-.1-.4L34.2 12.9h2.5l21.1 35.7h-15l1.3-2.2zM21 48.7l.8-1.3.8 1.3H21z' fill='%23fff'/%3E%3C/svg%3E");
}
.%prefix%-logo.login {
    width: 119px;
    height: 34px;
    background-image: url("data:image/svg+xml;base64,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");
}


.%prefix%-logo.loading {
    border-radius: 100%;
    background-color: #3650A2;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0.5 0.5 45 45' xmlns='http://www.w3.org/2000/svg' stroke='%23fff'%3E%3Cg fill='none' fill-rule='evenodd' transform='translate(1 1)' stroke-width='2'%3E%3Ccircle cx='22' cy='22' r='6' stroke-opacity='0'%3E%3Canimate attributeName='r' begin='1.5s' dur='3s' values='6;22' calcMode='linear' repeatCount='indefinite' /%3E%3Canimate attributeName='stroke-opacity' begin='1.5s' dur='3s' values='1;0' calcMode='linear' repeatCount='indefinite' /%3E%3Canimate attributeName='stroke-width' begin='1.5s' dur='3s' values='2;0' calcMode='linear' repeatCount='indefinite' /%3E%3C/circle%3E%3Ccircle cx='22' cy='22' r='6' stroke-opacity='0'%3E%3Canimate attributeName='r' begin='3s' dur='3s' values='6;22' calcMode='linear' repeatCount='indefinite' /%3E%3Canimate attributeName='stroke-opacity' begin='3s' dur='3s' values='1;0' calcMode='linear' repeatCount='indefinite' /%3E%3Canimate attributeName='stroke-width' begin='3s' dur='3s' values='2;0' calcMode='linear' repeatCount='indefinite' /%3E%3C/circle%3E%3Ccircle cx='22' cy='22' r='8'%3E%3Canimate attributeName='r' begin='0s' dur='1.5s' values='6;1;2;3;4;5;6' calcMode='linear' repeatCount='indefinite' /%3E%3C/circle%3E%3C/g%3E%3C/svg%3E");
}

.%prefix%-logo.error {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='70' height='70' fill='none'%3E%3Ccircle cx='35' cy='35' r='35' fill='%23fc3d39'/%3E%3Cpath fill-rule='evenodd' d='M32.88 17.614c.715-.403 1.522-.614 2.343-.614s1.628.212 2.343.614 1.314.983 1.74 1.685l.005.008 13.483 22.508.013.022c.417.722.638 1.541.64 2.375s-.214 1.654-.627 2.378-1.008 1.328-1.727 1.751-1.535.65-2.369.659h-.017-26.983c-.834-.009-1.651-.237-2.369-.659s-1.314-1.027-1.727-1.751-.629-1.544-.627-2.378.223-1.653.64-2.375l.013-.022L31.14 19.299c.426-.702 1.025-1.282 1.74-1.685zm2.343 2.569a1.59 1.59 0 0 0-1.359.763L20.392 43.438a1.59 1.59 0 0 0-.208.782c-.001.278.071.551.209.793a1.59 1.59 0 0 0 1.358.803h26.945a1.59 1.59 0 0 0 1.358-.803 1.59 1.59 0 0 0 .209-.793c-.001-.274-.073-.544-.208-.782L36.584 20.95c-.144-.236-.343-.428-.58-.561a1.59 1.59 0 0 0-.781-.205zm0 6.531a1.59 1.59 0 0 1 1.592 1.592v6.367a1.59 1.59 0 1 1-3.184 0v-6.367a1.59 1.59 0 0 1 1.592-1.592zm-1.592 14.326a1.59 1.59 0 0 1 1.592-1.592h.016a1.59 1.59 0 1 1 0 3.184h-.016a1.59 1.59 0 0 1-1.592-1.592z' fill='%23fff'/%3E%3C/svg%3E");
}

.%prefix%-logo.warning {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='70' height='70' fill='none' %3E%3Ccircle cx='35' cy='35' r='35' fill='%23f8b826'/%3E%3Cpath d='M35 20c-8.284 0-15 6.716-15 15s6.716 15 15 15 15-6.716 15-15-6.716-15-15-15zM16.667 35c0-10.125 8.208-18.333 18.333-18.333S53.333 24.875 53.333 35 45.125 53.334 35 53.334 16.667 45.126 16.667 35zM35 26.667c.921 0 1.667.746 1.667 1.667V35A1.67 1.67 0 0 1 35 36.667c-.921 0-1.667-.746-1.667-1.667v-6.667c0-.921.746-1.667 1.667-1.667zm-1.667 15A1.67 1.67 0 0 1 35 40h.017c.921 0 1.667.746 1.667 1.667s-.746 1.667-1.667 1.667H35a1.67 1.67 0 0 1-1.667-1.667z' fill-rule='evenodd' fill='%23fff'/%3E%3C/svg%3E");
}

.%prefix%-logo.success {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 70 70'%3E%3Cdefs/%3E%3Ccircle cx='35' cy='35' r='35' fill='%233DC55D'/%3E%3Cpath fill='%23fff' d='M30.9 49.7a2 2 0 001.8-1L48 24.9c.3-.5.4-1 .4-1.4 0-1-.7-1.7-1.7-1.7-.8 0-1.2.3-1.6 1L30.8 45.4 23.5 36c-.5-.6-1-.9-1.6-.9-1 0-1.8.8-1.8 1.8 0 .4.2.9.6 1.3L29 48.7c.6.7 1.1 1 1.9 1z'/%3E%3C/svg%3E");
}

.%prefix%-logo.fuel {
    background-image: url("data:image/svg+xml,%3Csvg width='70' height='70' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0)'%3E%3Cpath d='M69.23 30.31l-8.46-11a6.58 6.58 0 00-3.4-2.19L35.91 12.1a7.16 7.16 0 00-1.6-.16 7.73 7.73 0 00-2.18.28l-5.1 1.57a1.76 1.76 0 00-.17-1.4l-1.46-2.5a1.76 1.76 0 00-2.06-.8l-9.98 3.2a1.76 1.76 0 00-1.23 1.74l.13 3.39c.01.27.09.54.22.78l-2.28.7a5.85 5.85 0 00-3.24 2.7L.48 34.23a4.96 4.96 0 00.14 4.53l3.5 5.83a4.49 4.49 0 004.19 2.05l9.29-1.13a4.79 4.79 0 002.54 3.78l3.55 1.68a8.9 8.9 0 003.39.73h.27l8.06-.45c.26.04.5.13.73.28l8.02 8c1 .92 2.3 1.45 3.67 1.47.14 0 .28 0 .42-.03l6.6-.68a2.85 2.85 0 002.5-1.8c.36-1 .1-2.12-.73-3l-2.78-2.96 5.26-1.56a4.35 4.35 0 003-3.64l.41-4.29c.09-.42.26-.82.52-1.16l6-6.66a3.91 3.91 0 00.2-4.9z' fill='%23fff'/%3E%3Cpath d='M49.08 29.96a1.72 1.72 0 00-.92-2.25 1.72 1.72 0 00-1.3 3.17 1.7 1.7 0 002.22-.92zM27.54 26.41a1.02 1.02 0 001-1.23 1.02 1.02 0 00-1.85-.37 1.03 1.03 0 00.46 1.52c.12.05.25.08.39.08zM37.05 21.67a1.03 1.03 0 000-2.06 1.03 1.03 0 000 2.06zM21.54 35c-4.62 0-10.44 2.04-13.52 3.24a.68.68 0 00-.24.16.68.68 0 00-.18.53c.01.1.04.19.1.27l1.28 2.16c.07.1.17.2.28.25.11.06.24.08.37.07l9.15-1.13.45-.03a3.64 3.64 0 013.67 3.55v.3a.67.67 0 00.39.59l2.9 1.38c.42.16.86.26 1.3.28h.05l7.74-.45h.23c1.45.03 2.83.59 3.9 1.58l7.68 7.65c.1.06.22.1.35.12l1.67-.2c.06-.01.12-.03.16-.07a.32.32 0 00.1-.13.33.33 0 00-.03-.33l-.82-1.21a3.22 3.22 0 01-.01-4.58c.4-.4.9-.68 1.45-.82l6.47-1.92a.4.4 0 00.19-.13.41.41 0 00.08-.22l.35-3.53c.01-.06 0-.13-.02-.2a.41.41 0 00-.28-.24.37.37 0 00-.2.01c-2.27.62-6.27 1.6-8.73 1.6C37.4 43.53 29.68 35 21.54 35zm8.97 7.49a.8.8 0 01-.27.28c-.2.13-.45.19-.68.17a1.05 1.05 0 01-.77-1.62.73.73 0 01.28-.28 1.05 1.05 0 011.44 1.45zm17.6 3.72a.76.76 0 01-.27.28 1.05 1.05 0 01-1.17-1.73 1.05 1.05 0 011.32.13 1.04 1.04 0 01.13 1.32z' fill='%23F8B826'/%3E%3Cpath d='M21.54 35c-4.85 0-11.03 2.25-13.96 3.42a.29.29 0 00-.15.16c-.01.03-.02.07-.01.11 0 .04.01.07.03.1L9 41.38c.06.11.16.2.27.26.12.05.25.07.37.06l9.15-1.13c.15-.02.3-.02.46-.03a3.63 3.63 0 013.66 3.54l.02.56c0 .04.02.09.05.13.02.04.06.07.1.1l3.13 1.48c.41.16.85.26 1.3.28h.04l7.74-.45h.23c1.45.03 2.84.59 3.9 1.58l7.68 7.65c.1.06.23.1.35.12l2.22-.27-1.58-1.66c-.82-.88-.7-2.05-.4-3.13a3.28 3.28 0 012.3-2.27l6.71-2 .38-3.8.06-.58c-2.13.59-6.6 1.74-9.3 1.74C37.4 43.54 29.69 35 21.54 35zm8.89 7.62a.38.38 0 01-.07.07c-1.07.77-2.26-.42-1.49-1.5a.25.25 0 01.06-.07c1.08-.77 2.27.42 1.5 1.5zm17.6 3.72a.25.25 0 01-.06.06c-1.07.78-2.26-.42-1.49-1.5a.25.25 0 01.07-.06c1.07-.78 2.26.42 1.49 1.5z' fill='%23E99123'/%3E%3Cpath d='M47.53 34.9c-10.41 0-19.22-4.65-25.54-4.65-4.44 0-11.97 3.38-15.28 5.83a.55.55 0 00-.18.69l.35.78a.55.55 0 00.72.28c2.84-1.18 8.9-3.42 13.94-3.42 8.4 0 16.15 7.48 25.16 7.48 4 0 9.88-1.58 11.41-2.82.67-.7 2.86-3.32 4.4-4.93a.67.67 0 00.1-.8.67.67 0 00-.32-.28.65.65 0 00-.42-.03 64.85 64.85 0 01-14.34 1.86zm-.47 3.95c-.14.3-.38.55-.68.7a1.73 1.73 0 01-2.34-2.36c.14-.3.38-.55.68-.7a1.72 1.72 0 012.04.32 1.75 1.75 0 01.3 2.04z' fill='%23F8B826'/%3E%3Cpath d='M68.34 30.87l-8.46-11a5.52 5.52 0 00-2.77-1.78l-21.46-5.03a7.08 7.08 0 00-3.2.1l-7.55 2.3-.01.01h-.01l-.5-1.3v-.01l1.13-.4a.65.65 0 00.36-.93l-1.47-2.5a.64.64 0 00-.73-.27l-9.98 3.17a.64.64 0 00-.44.63l.13 3.39a.64.64 0 00.63.6c.07 0 .15-.02.22-.04l2.53-.91h.02l.44.87v.02l-6.7 2.05c-1.08.38-2 1.15-2.56 2.15L1.47 34.61a3.8 3.8 0 00.11 3.43l3.5 5.84a3.32 3.32 0 003.1 1.51l10.23-1.26a.2.2 0 01.2.04.2.2 0 01.06.08l.02.1.03.9a3.69 3.69 0 001.9 2.88l3.56 1.69c.98.42 2.05.63 3.12.61l8.06-.44c.58.02 1.13.23 1.57.6l8.03 8a4.38 4.38 0 003.18 1.12l6.6-.69c.77-.07 1.33-.46 1.55-1.07.23-.6.05-1.26-.48-1.82l-4.02-4.26-.01-.01v-.01l7-2.09a3.25 3.25 0 002.2-2.66l.43-4.3a4 4 0 01.8-1.81l6.01-6.67a2.76 2.76 0 00.12-3.45zM15.14 15.2l-.03-.51c0-.08.02-.16.06-.22.04-.07.1-.11.18-.14l7.73-2.5a.35.35 0 01.41.16l.13.23c.03.05.04.1.05.15 0 .05 0 .1-.02.14a.35.35 0 01-.08.13.38.38 0 01-.12.09L15.6 15.5a.34.34 0 01-.31-.04.36.36 0 01-.16-.27zm51.53 17.75l-6 6.66a5.95 5.95 0 00-1.32 3l-.42 4.3a1.22 1.22 0 01-.73.87l-7.32 2.17a1.8 1.8 0 00-1.34 2.28c.1.32.27.6.52.83l3.19 3.37a.4.4 0 01.09.43.42.42 0 01-.14.19.42.42 0 01-.21.08l-5.06.53c-.55 0-1.09-.19-1.51-.54l-8.03-8a4.62 4.62 0 00-3.14-1.2l-8.07.45c-.73.01-1.45-.13-2.12-.41l-3.56-1.7a1.4 1.4 0 01-.72-1.08l-.04-.9a2.35 2.35 0 00-.8-1.68 2.24 2.24 0 00-1.78-.53L7.92 43.32a1.32 1.32 0 01-1.07-.53l-3.49-5.82a1.78 1.78 0 01-.05-1.4L9.8 22.94c.32-.5.78-.89 1.33-1.1l21.94-6.67c.69-.18 1.42-.2 2.12-.07l21.46 5.02c.62.19 1.18.55 1.6 1.03l8.47 11c.08.11.12.25.12.4a.69.69 0 01-.16.39z' fill='%2329363F'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0'%3E%3Cpath fill='%23fff' transform='translate(0 9)' d='M0 0h70v52H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
}

.%prefix%-request {
    padding: 20px 52px;
    border-radius: 20px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    background: white;
}
.%prefix%-request.login{
    padding-top: 0;
    padding-bottom: 0;
}
.%prefix%-info {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.%prefix%-title {
    color: #000000;
    font-size: 25px;
    margin: 20px auto;
    font-weight: 700;
    line-height: 30px;
    letter-spacing: 0.5px;
}

.%prefix%-subtitle {
    margin-top: 14px;
    color: #8486A4;
    text-align: center;
    line-height: 1.4;
    word-break: break-word;
    font-size: 16px;
}

.%prefix%-subtitle a {
    color: #000000;
    cursor: pointer;
    text-decoration: underline;
}

.%prefix%-manual {
    color: #007AFF;
    cursor: pointer;
    
}

.%prefix%-info hr {
    border: 0;
    height: 1px;
    width: 100%;
    background: #EFF1F7;
    margin: 24px 0 14px;
}

.%prefix%-uri {
    width: 100%;
}

.%prefix%-button {
    color: #FFF;
    background: #536EF3;
    text-decoration: none;
    font-weight: 500;
    font-size: 16px;
    flex-grow: 1;
    flex: 1;
    width: 100%;
    line-height:  40px;
    height: 40px;
    border-radius: 8px;
    text-align: center;
    display: block;
    margin-top: 14px;
    cursor: pointer;
    display:none;
}

.%prefix%-button:hover {
  background: 
}

.%prefix%-qr {
    position: relative;
    width: 100%;
    margin-top: 14px;
    border-radius: 12px;
    background: #FFFFFF;
    transition: all 400ms ease-in-out;
    transform: scale(1) translateY(0);
}

.%prefix%-qr svg {
    width: 100%;
    display: block;
    cursor: zoom-in;
    shape-rendering: crispEdges;
}

.%prefix%-qr.zoom {
    transform: scale(2) translateY(-25px);
    border: 2px solid #536EF3;
    box-sizing: border-box;
    box-shadow: 0px 4px 154px rgba(0, 0, 0, 0.35);
    border-radius: 8px;
    padding: 5px;
    z-index: 2147483647;
}

.%prefix%-qr.zoom svg {
    cursor: zoom-out;
}

.%prefix%-qr svg rect {
    fill: black;
}

.%prefix%-copy {
    margin-top:14px;
    width: 100%;
    color: #8486A4;
    text-align: center;
    opacity: 1;
    transition: all 200ms ease-in-out;
    transition-delay: 400ms;
    font-size:14px;
}

.%prefix%-qr.zoom .%prefix%-copy {
    transition-delay: 0ms;
    background: transparent;
    opacity: 0;
}

.%prefix%-copy span,
.%prefix%-copy a {
    display: inline-block;
    background: white;
    padding: 0 20px;
    color: #8486A4;
    cursor: pointer;
    text-decoration: none;
}

.%prefix%-copy span {
    display: none;
    color: #5C5C5C;
    cursor: default;
}

.%prefix%-copy.copied a {
    display: none;
}

.%prefix%-copy.copied span {
    display: inline-block;
}

.%prefix%-download{
    color: #070847;
    font-size: 14px;
    margin-top:18px;
}

.%prefix%-download a{
    color: #536EF3;
    text-decoration: none;
}

// .%prefix%-copy span:before,
// .%prefix%-copy a:before {
//     content: '';
//     display: inline-block;
//     width: 26px;
//     height: 16px;
//     position: relative;
//     top: 2px;
//     background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='17' height='17' fill='none'%3E%3Cg clip-path='url(%23A)' stroke='%23157efa' stroke-linejoin='round' stroke-width='2'%3E%3Cpath d='M13.83 6.5h-6c-.73 0-1.33.6-1.33 1.33v6c0 .74.6 1.34 1.33 1.34h6c.74 0 1.34-.6 1.34-1.34v-6c0-.73-.6-1.33-1.34-1.33z'/%3E%3Cpath d='M3.83 10.5h-.66a1.33 1.33 0 01-1.34-1.33v-6a1.33 1.33 0 011.34-1.34h6a1.33 1.33 0 011.33 1.34v.66' stroke-linecap='round'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='A'%3E%3Cpath fill='%23fff' transform='translate(.5 .5)' d='M0 0h16v16H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
//     background-repeat: no-repeat;
// }

.%prefix%-copy span:before {
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.33 4L6 11.33 2.67 8' stroke='%23157EFA' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    top: 3.5px;
}

.%prefix%-footnote {
    text-align: center;
    width: 100%;
    position: absolute;
    bottom: -26px;
    left: 0;
    color: white;
    display: none;
}

.%prefix%-footnote  .%prefix%-download {
    color: white;
}

.%prefix%-wskeepalive {
    display: none;
}


// @media (prefers-color-scheme: dark) {
//     .%prefix%-info hr,
//     .%prefix%-inner {
//         background: #262D43;
//         color: white;
//     }
//     .%prefix%-request,
//     .%prefix%-qr a,
//     .%prefix%-qr span,
//     .%prefix%-qr {
//         background: #131B33;
//     }
//     .%prefix%-title {
//         color: #FCFCFC;
//     }
//     .%prefix%-qr span,
//     .%prefix%-subtitle {
//         color: #B8C0DA;
//     }
//     .%prefix%-subtitle a {
//         color: #FCFCFC;
//     }
//     .%prefix%-qr svg rect {
//         fill: white;
//     }
//     .%prefix%-version {
//         color: #546AAF;
//     }
//     .%prefix%-qr a,
//     .%prefix%-manual,
//     .%prefix%-button {
//         color: #FCFCFC;
//     }
//     .%prefix%-button {
//         background: #262D43;
//         border: 1px solid #262D43;
//     }
//     .%prefix%-qr {
//         border-color: #262D43;
//     }
//     .%prefix%-qr.zoom {
//         border-color: #536EF3;
//     }
//     .%prefix%-copy a:before {
//         background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='17' height='17' fill='none'%3E%3Cg clip-path='url(%23A)' stroke='%23fff' stroke-linejoin='round' stroke-width='2'%3E%3Cpath d='M13.83 6.5h-6c-.73 0-1.33.6-1.33 1.33v6c0 .74.6 1.34 1.33 1.34h6c.74 0 1.34-.6 1.34-1.34v-6c0-.73-.6-1.33-1.34-1.33z'/%3E%3Cpath d='M3.83 10.5h-.66a1.33 1.33 0 01-1.34-1.33v-6a1.33 1.33 0 011.34-1.34h6a1.33 1.33 0 011.33 1.34v.66' stroke-linecap='round'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='A'%3E%3Cpath fill='%23fff' transform='translate(.5 .5)' d='M0 0h16v16H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
//     }
//     .%prefix%-copy span:before {
//         background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.33 4L6 11.33 2.67 8' stroke='%23fff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
//     }
//     .%prefix%-button:hover {
//         color: #FCFCFC;
//         border: 1px solid #FCFCFC;
//         background: #333A50;
//     }
//     .%prefix%-close {
//         background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M.57 12.1a.96.96 0 000 1.34c.37.36 1 .36 1.34 0L7 8.37l5.09 5.09c.36.35.97.35 1.34-.01a.96.96 0 000-1.34L8.34 7.01l5.08-5.08a.95.95 0 000-1.33.97.97 0 00-1.34-.01L6.99 5.68 1.91.59a.96.96 0 00-1.33 0 .97.97 0 00-.01 1.34l5.09 5.08-5.1 5.1z' fill='%23FCFCFC'/%3E%3C/svg%3E");
//     }
//     .%prefix%-close:hover {
//         background-color: black;
//     }
// }

// @media (max-height: 600px) { .%prefix%-inner { transform: scale(0.9); } }
@media (max-height: 540px) { .%prefix%-inner { transform: scale(0.8); } }
@media (max-height: 480px) { .%prefix%-inner { transform: scale(0.7); } }
@media (max-height: 420px) { .%prefix%-inner { transform: scale(0.6); } }
@media (max-height: 360px) { .%prefix%-inner { transform: scale(0.5); } }
@media (max-height: 300px) { .%prefix%-inner { transform: scale(0.4); } }

@media (max-width: 600px) and (orientation: portrait) {
    .%prefix%-qr.zoom {
        transform: scale(1.5) translateY(-25px);
    }
    .%prefix%-info .%prefix%-download {
        display:none;
    }
    .%prefix%-button {
        display: block;
    }

    .%prefix%-footnote  {
        display: block;
    }

    .%prefix%-request{
        padding-left: 24px;
        padding-right: 24px;
    }
    .%prefix%-subtitle{
        font-size: 14px;
    }
    .%prefix%-close{
        display: none;
    }
}

@media (max-width: 450px) and (orientation: portrait) {
    .%prefix%-qr.zoom {
        transform: scale(1.3) translateY(-25px);
    }
}

`;

var ErrorCorrectLevel = {
    L: 1,
    M: 0,
    Q: 3,
    H: 2,
};

var Mode = {
    MODE_NUMBER: 1 << 0,
    MODE_ALPHA_NUM: 1 << 1,
    MODE_8BIT_BYTE: 1 << 2,
    MODE_KANJI: 1 << 3,
};

class QR8bitByte {
    constructor(data) {
        this.mode = Mode.MODE_8BIT_BYTE;
        this.data = data;
    }
    getLength() {
        return this.data.length;
    }
    write(buffer) {
        for (let i = 0; i < this.data.length; i++) {
            // not JIS ...
            buffer.put(this.data.charCodeAt(i), 8);
        }
    }
}

class QRBitBuffer {
    constructor() {
        this.buffer = [];
        this.length = 0;
    }
    get(index) {
        const bufIndex = Math.floor(index / 8);
        return ((this.buffer[bufIndex] >>> (7 - (index % 8))) & 1) == 1;
    }
    put(num, length) {
        for (let i = 0; i < length; i++) {
            this.putBit(((num >>> (length - i - 1)) & 1) == 1);
        }
    }
    getLengthInBits() {
        return this.length;
    }
    putBit(bit) {
        const bufIndex = Math.floor(this.length / 8);
        if (this.buffer.length <= bufIndex) {
            this.buffer.push(0);
        }
        if (bit) {
            this.buffer[bufIndex] |= 0x80 >>> this.length % 8;
        }
        this.length++;
    }
}

const QRMath = {
    glog: function (n) {
        if (n < 1) {
            throw new Error('glog(' + n + ')');
        }
        return QRMath.LOG_TABLE[n];
    },
    gexp: function (n) {
        while (n < 0) {
            n += 255;
        }
        while (n >= 256) {
            n -= 255;
        }
        return QRMath.EXP_TABLE[n];
    },
    EXP_TABLE: new Array(256),
    LOG_TABLE: new Array(256),
};
for (let i = 0; i < 8; i++) {
    QRMath.EXP_TABLE[i] = 1 << i;
}
for (let i = 8; i < 256; i++) {
    QRMath.EXP_TABLE[i] =
        QRMath.EXP_TABLE[i - 4] ^
            QRMath.EXP_TABLE[i - 5] ^
            QRMath.EXP_TABLE[i - 6] ^
            QRMath.EXP_TABLE[i - 8];
}
for (let i = 0; i < 255; i++) {
    QRMath.LOG_TABLE[QRMath.EXP_TABLE[i]] = i;
}

class QRPolynomial {
    constructor(num, shift) {
        if (num.length == undefined) {
            throw new Error(num.length + '/' + shift);
        }
        let offset = 0;
        while (offset < num.length && num[offset] == 0) {
            offset++;
        }
        this.num = new Array(num.length - offset + shift);
        for (let i = 0; i < num.length - offset; i++) {
            this.num[i] = num[i + offset];
        }
    }
    get(index) {
        return this.num[index];
    }
    getLength() {
        return this.num.length;
    }
    multiply(e) {
        const num = new Array(this.getLength() + e.getLength() - 1);
        for (let i = 0; i < this.getLength(); i++) {
            for (let j = 0; j < e.getLength(); j++) {
                num[i + j] ^= QRMath.gexp(QRMath.glog(this.get(i)) + QRMath.glog(e.get(j)));
            }
        }
        return new QRPolynomial(num, 0);
    }
    mod(e) {
        if (this.getLength() - e.getLength() < 0) {
            return this;
        }
        const ratio = QRMath.glog(this.get(0)) - QRMath.glog(e.get(0));
        const num = new Array(this.getLength());
        for (let i = 0; i < this.getLength(); i++) {
            num[i] = this.get(i);
        }
        for (let i = 0; i < e.getLength(); i++) {
            num[i] ^= QRMath.gexp(QRMath.glog(e.get(i)) + ratio);
        }
        // recursive call
        return new QRPolynomial(num, 0).mod(e);
    }
}

// ErrorCorrectLevel
class QRRSBlock {
    constructor(totalCount, dataCount) {
        this.totalCount = totalCount;
        this.dataCount = dataCount;
    }
    static getRSBlocks(typeNumber, errorCorrectLevel) {
        const rsBlock = QRRSBlock.getRsBlockTable(typeNumber, errorCorrectLevel);
        if (rsBlock == undefined) {
            throw new Error('bad rs block @ typeNumber:' +
                typeNumber +
                '/errorCorrectLevel:' +
                errorCorrectLevel);
        }
        const length = rsBlock.length / 3;
        const list = [];
        for (let i = 0; i < length; i++) {
            const count = rsBlock[i * 3 + 0];
            const totalCount = rsBlock[i * 3 + 1];
            const dataCount = rsBlock[i * 3 + 2];
            for (let j = 0; j < count; j++) {
                list.push(new QRRSBlock(totalCount, dataCount));
            }
        }
        return list;
    }
    static getRsBlockTable(typeNumber, errorCorrectLevel) {
        switch (errorCorrectLevel) {
            case ErrorCorrectLevel.L:
                return QRRSBlock.RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 0];
            case ErrorCorrectLevel.M:
                return QRRSBlock.RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 1];
            case ErrorCorrectLevel.Q:
                return QRRSBlock.RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 2];
            case ErrorCorrectLevel.H:
                return QRRSBlock.RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 3];
            default:
                return undefined;
        }
    }
}
QRRSBlock.RS_BLOCK_TABLE = [
    // L
    // M
    // Q
    // H
    // 1
    [1, 26, 19],
    [1, 26, 16],
    [1, 26, 13],
    [1, 26, 9],
    // 2
    [1, 44, 34],
    [1, 44, 28],
    [1, 44, 22],
    [1, 44, 16],
    // 3
    [1, 70, 55],
    [1, 70, 44],
    [2, 35, 17],
    [2, 35, 13],
    // 4
    [1, 100, 80],
    [2, 50, 32],
    [2, 50, 24],
    [4, 25, 9],
    // 5
    [1, 134, 108],
    [2, 67, 43],
    [2, 33, 15, 2, 34, 16],
    [2, 33, 11, 2, 34, 12],
    // 6
    [2, 86, 68],
    [4, 43, 27],
    [4, 43, 19],
    [4, 43, 15],
    // 7
    [2, 98, 78],
    [4, 49, 31],
    [2, 32, 14, 4, 33, 15],
    [4, 39, 13, 1, 40, 14],
    // 8
    [2, 121, 97],
    [2, 60, 38, 2, 61, 39],
    [4, 40, 18, 2, 41, 19],
    [4, 40, 14, 2, 41, 15],
    // 9
    [2, 146, 116],
    [3, 58, 36, 2, 59, 37],
    [4, 36, 16, 4, 37, 17],
    [4, 36, 12, 4, 37, 13],
    // 10
    [2, 86, 68, 2, 87, 69],
    [4, 69, 43, 1, 70, 44],
    [6, 43, 19, 2, 44, 20],
    [6, 43, 15, 2, 44, 16],
    // 11
    [4, 101, 81],
    [1, 80, 50, 4, 81, 51],
    [4, 50, 22, 4, 51, 23],
    [3, 36, 12, 8, 37, 13],
    // 12
    [2, 116, 92, 2, 117, 93],
    [6, 58, 36, 2, 59, 37],
    [4, 46, 20, 6, 47, 21],
    [7, 42, 14, 4, 43, 15],
    // 13
    [4, 133, 107],
    [8, 59, 37, 1, 60, 38],
    [8, 44, 20, 4, 45, 21],
    [12, 33, 11, 4, 34, 12],
    // 14
    [3, 145, 115, 1, 146, 116],
    [4, 64, 40, 5, 65, 41],
    [11, 36, 16, 5, 37, 17],
    [11, 36, 12, 5, 37, 13],
    // 15
    [5, 109, 87, 1, 110, 88],
    [5, 65, 41, 5, 66, 42],
    [5, 54, 24, 7, 55, 25],
    [11, 36, 12],
    // 16
    [5, 122, 98, 1, 123, 99],
    [7, 73, 45, 3, 74, 46],
    [15, 43, 19, 2, 44, 20],
    [3, 45, 15, 13, 46, 16],
    // 17
    [1, 135, 107, 5, 136, 108],
    [10, 74, 46, 1, 75, 47],
    [1, 50, 22, 15, 51, 23],
    [2, 42, 14, 17, 43, 15],
    // 18
    [5, 150, 120, 1, 151, 121],
    [9, 69, 43, 4, 70, 44],
    [17, 50, 22, 1, 51, 23],
    [2, 42, 14, 19, 43, 15],
    // 19
    [3, 141, 113, 4, 142, 114],
    [3, 70, 44, 11, 71, 45],
    [17, 47, 21, 4, 48, 22],
    [9, 39, 13, 16, 40, 14],
    // 20
    [3, 135, 107, 5, 136, 108],
    [3, 67, 41, 13, 68, 42],
    [15, 54, 24, 5, 55, 25],
    [15, 43, 15, 10, 44, 16],
    // 21
    [4, 144, 116, 4, 145, 117],
    [17, 68, 42],
    [17, 50, 22, 6, 51, 23],
    [19, 46, 16, 6, 47, 17],
    // 22
    [2, 139, 111, 7, 140, 112],
    [17, 74, 46],
    [7, 54, 24, 16, 55, 25],
    [34, 37, 13],
    // 23
    [4, 151, 121, 5, 152, 122],
    [4, 75, 47, 14, 76, 48],
    [11, 54, 24, 14, 55, 25],
    [16, 45, 15, 14, 46, 16],
    // 24
    [6, 147, 117, 4, 148, 118],
    [6, 73, 45, 14, 74, 46],
    [11, 54, 24, 16, 55, 25],
    [30, 46, 16, 2, 47, 17],
    // 25
    [8, 132, 106, 4, 133, 107],
    [8, 75, 47, 13, 76, 48],
    [7, 54, 24, 22, 55, 25],
    [22, 45, 15, 13, 46, 16],
    // 26
    [10, 142, 114, 2, 143, 115],
    [19, 74, 46, 4, 75, 47],
    [28, 50, 22, 6, 51, 23],
    [33, 46, 16, 4, 47, 17],
    // 27
    [8, 152, 122, 4, 153, 123],
    [22, 73, 45, 3, 74, 46],
    [8, 53, 23, 26, 54, 24],
    [12, 45, 15, 28, 46, 16],
    // 28
    [3, 147, 117, 10, 148, 118],
    [3, 73, 45, 23, 74, 46],
    [4, 54, 24, 31, 55, 25],
    [11, 45, 15, 31, 46, 16],
    // 29
    [7, 146, 116, 7, 147, 117],
    [21, 73, 45, 7, 74, 46],
    [1, 53, 23, 37, 54, 24],
    [19, 45, 15, 26, 46, 16],
    // 30
    [5, 145, 115, 10, 146, 116],
    [19, 75, 47, 10, 76, 48],
    [15, 54, 24, 25, 55, 25],
    [23, 45, 15, 25, 46, 16],
    // 31
    [13, 145, 115, 3, 146, 116],
    [2, 74, 46, 29, 75, 47],
    [42, 54, 24, 1, 55, 25],
    [23, 45, 15, 28, 46, 16],
    // 32
    [17, 145, 115],
    [10, 74, 46, 23, 75, 47],
    [10, 54, 24, 35, 55, 25],
    [19, 45, 15, 35, 46, 16],
    // 33
    [17, 145, 115, 1, 146, 116],
    [14, 74, 46, 21, 75, 47],
    [29, 54, 24, 19, 55, 25],
    [11, 45, 15, 46, 46, 16],
    // 34
    [13, 145, 115, 6, 146, 116],
    [14, 74, 46, 23, 75, 47],
    [44, 54, 24, 7, 55, 25],
    [59, 46, 16, 1, 47, 17],
    // 35
    [12, 151, 121, 7, 152, 122],
    [12, 75, 47, 26, 76, 48],
    [39, 54, 24, 14, 55, 25],
    [22, 45, 15, 41, 46, 16],
    // 36
    [6, 151, 121, 14, 152, 122],
    [6, 75, 47, 34, 76, 48],
    [46, 54, 24, 10, 55, 25],
    [2, 45, 15, 64, 46, 16],
    // 37
    [17, 152, 122, 4, 153, 123],
    [29, 74, 46, 14, 75, 47],
    [49, 54, 24, 10, 55, 25],
    [24, 45, 15, 46, 46, 16],
    // 38
    [4, 152, 122, 18, 153, 123],
    [13, 74, 46, 32, 75, 47],
    [48, 54, 24, 14, 55, 25],
    [42, 45, 15, 32, 46, 16],
    // 39
    [20, 147, 117, 4, 148, 118],
    [40, 75, 47, 7, 76, 48],
    [43, 54, 24, 22, 55, 25],
    [10, 45, 15, 67, 46, 16],
    // 40
    [19, 148, 118, 6, 149, 119],
    [18, 75, 47, 31, 76, 48],
    [34, 54, 24, 34, 55, 25],
    [20, 45, 15, 61, 46, 16],
];

const QRMaskPattern = {
    PATTERN000: 0,
    PATTERN001: 1,
    PATTERN010: 2,
    PATTERN011: 3,
    PATTERN100: 4,
    PATTERN101: 5,
    PATTERN110: 6,
    PATTERN111: 7,
};
const QRUtil = {
    PATTERN_POSITION_TABLE: [
        [],
        [6, 18],
        [6, 22],
        [6, 26],
        [6, 30],
        [6, 34],
        [6, 22, 38],
        [6, 24, 42],
        [6, 26, 46],
        [6, 28, 50],
        [6, 30, 54],
        [6, 32, 58],
        [6, 34, 62],
        [6, 26, 46, 66],
        [6, 26, 48, 70],
        [6, 26, 50, 74],
        [6, 30, 54, 78],
        [6, 30, 56, 82],
        [6, 30, 58, 86],
        [6, 34, 62, 90],
        [6, 28, 50, 72, 94],
        [6, 26, 50, 74, 98],
        [6, 30, 54, 78, 102],
        [6, 28, 54, 80, 106],
        [6, 32, 58, 84, 110],
        [6, 30, 58, 86, 114],
        [6, 34, 62, 90, 118],
        [6, 26, 50, 74, 98, 122],
        [6, 30, 54, 78, 102, 126],
        [6, 26, 52, 78, 104, 130],
        [6, 30, 56, 82, 108, 134],
        [6, 34, 60, 86, 112, 138],
        [6, 30, 58, 86, 114, 142],
        [6, 34, 62, 90, 118, 146],
        [6, 30, 54, 78, 102, 126, 150],
        [6, 24, 50, 76, 102, 128, 154],
        [6, 28, 54, 80, 106, 132, 158],
        [6, 32, 58, 84, 110, 136, 162],
        [6, 26, 54, 82, 110, 138, 166],
        [6, 30, 58, 86, 114, 142, 170],
    ],
    G15: (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | (1 << 0),
    G18: (1 << 12) | (1 << 11) | (1 << 10) | (1 << 9) | (1 << 8) | (1 << 5) | (1 << 2) | (1 << 0),
    G15_MASK: (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1),
    getBCHTypeInfo: function (data) {
        let d = data << 10;
        while (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G15) >= 0) {
            d ^= QRUtil.G15 << (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G15));
        }
        return ((data << 10) | d) ^ QRUtil.G15_MASK;
    },
    getBCHTypeNumber: function (data) {
        let d = data << 12;
        while (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G18) >= 0) {
            d ^= QRUtil.G18 << (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G18));
        }
        return (data << 12) | d;
    },
    getBCHDigit: function (data) {
        let digit = 0;
        while (data != 0) {
            digit++;
            data >>>= 1;
        }
        return digit;
    },
    getPatternPosition: function (typeNumber) {
        return QRUtil.PATTERN_POSITION_TABLE[typeNumber - 1];
    },
    getMask: function (maskPattern, i, j) {
        switch (maskPattern) {
            case QRMaskPattern.PATTERN000:
                return (i + j) % 2 == 0;
            case QRMaskPattern.PATTERN001:
                return i % 2 == 0;
            case QRMaskPattern.PATTERN010:
                return j % 3 == 0;
            case QRMaskPattern.PATTERN011:
                return (i + j) % 3 == 0;
            case QRMaskPattern.PATTERN100:
                return (Math.floor(i / 2) + Math.floor(j / 3)) % 2 == 0;
            case QRMaskPattern.PATTERN101:
                return ((i * j) % 2) + ((i * j) % 3) == 0;
            case QRMaskPattern.PATTERN110:
                return (((i * j) % 2) + ((i * j) % 3)) % 2 == 0;
            case QRMaskPattern.PATTERN111:
                return (((i * j) % 3) + ((i + j) % 2)) % 2 == 0;
            default:
                throw new Error('bad maskPattern:' + maskPattern);
        }
    },
    getErrorCorrectPolynomial: function (errorCorrectLength) {
        let a = new QRPolynomial([1], 0);
        for (let i = 0; i < errorCorrectLength; i++) {
            a = a.multiply(new QRPolynomial([1, QRMath.gexp(i)], 0));
        }
        return a;
    },
    getLengthInBits: function (mode, type) {
        if (1 <= type && type < 10) {
            // 1 - 9
            switch (mode) {
                case Mode.MODE_NUMBER:
                    return 10;
                case Mode.MODE_ALPHA_NUM:
                    return 9;
                case Mode.MODE_8BIT_BYTE:
                    return 8;
                case Mode.MODE_KANJI:
                    return 8;
                default:
                    throw new Error('mode:' + mode);
            }
        }
        else if (type < 27) {
            // 10 - 26
            switch (mode) {
                case Mode.MODE_NUMBER:
                    return 12;
                case Mode.MODE_ALPHA_NUM:
                    return 11;
                case Mode.MODE_8BIT_BYTE:
                    return 16;
                case Mode.MODE_KANJI:
                    return 10;
                default:
                    throw new Error('mode:' + mode);
            }
        }
        else if (type < 41) {
            // 27 - 40
            switch (mode) {
                case Mode.MODE_NUMBER:
                    return 14;
                case Mode.MODE_ALPHA_NUM:
                    return 13;
                case Mode.MODE_8BIT_BYTE:
                    return 16;
                case Mode.MODE_KANJI:
                    return 12;
                default:
                    throw new Error('mode:' + mode);
            }
        }
        else {
            throw new Error('type:' + type);
        }
    },
    getLostPoint: function (qrCode) {
        const moduleCount = qrCode.getModuleCount();
        let lostPoint = 0;
        // LEVEL1
        for (let row = 0; row < moduleCount; row++) {
            for (let col = 0; col < moduleCount; col++) {
                let sameCount = 0;
                const dark = qrCode.isDark(row, col);
                for (let r = -1; r <= 1; r++) {
                    if (row + r < 0 || moduleCount <= row + r) {
                        continue;
                    }
                    for (let c = -1; c <= 1; c++) {
                        if (col + c < 0 || moduleCount <= col + c) {
                            continue;
                        }
                        if (r == 0 && c == 0) {
                            continue;
                        }
                        if (dark == qrCode.isDark(row + r, col + c)) {
                            sameCount++;
                        }
                    }
                }
                if (sameCount > 5) {
                    lostPoint += 3 + sameCount - 5;
                }
            }
        }
        // LEVEL2
        for (let row = 0; row < moduleCount - 1; row++) {
            for (let col = 0; col < moduleCount - 1; col++) {
                let count = 0;
                if (qrCode.isDark(row, col))
                    count++;
                if (qrCode.isDark(row + 1, col))
                    count++;
                if (qrCode.isDark(row, col + 1))
                    count++;
                if (qrCode.isDark(row + 1, col + 1))
                    count++;
                if (count == 0 || count == 4) {
                    lostPoint += 3;
                }
            }
        }
        // LEVEL3
        for (let row = 0; row < moduleCount; row++) {
            for (let col = 0; col < moduleCount - 6; col++) {
                if (qrCode.isDark(row, col) &&
                    !qrCode.isDark(row, col + 1) &&
                    qrCode.isDark(row, col + 2) &&
                    qrCode.isDark(row, col + 3) &&
                    qrCode.isDark(row, col + 4) &&
                    !qrCode.isDark(row, col + 5) &&
                    qrCode.isDark(row, col + 6)) {
                    lostPoint += 40;
                }
            }
        }
        for (let col = 0; col < moduleCount; col++) {
            for (let row = 0; row < moduleCount - 6; row++) {
                if (qrCode.isDark(row, col) &&
                    !qrCode.isDark(row + 1, col) &&
                    qrCode.isDark(row + 2, col) &&
                    qrCode.isDark(row + 3, col) &&
                    qrCode.isDark(row + 4, col) &&
                    !qrCode.isDark(row + 5, col) &&
                    qrCode.isDark(row + 6, col)) {
                    lostPoint += 40;
                }
            }
        }
        // LEVEL4
        let darkCount = 0;
        for (let col = 0; col < moduleCount; col++) {
            for (let row = 0; row < moduleCount; row++) {
                if (qrCode.isDark(row, col)) {
                    darkCount++;
                }
            }
        }
        const ratio = Math.abs((100 * darkCount) / moduleCount / moduleCount - 50) / 5;
        lostPoint += ratio * 10;
        return lostPoint;
    },
};

class QRCode {
    constructor(typeNumber, errorCorrectLevel) {
        this.typeNumber = typeNumber;
        this.errorCorrectLevel = errorCorrectLevel;
        this.modules = null;
        this.moduleCount = 0;
        this.dataCache = null;
        this.dataList = [];
    }
    addData(data) {
        const newData = new QR8bitByte(data);
        this.dataList.push(newData);
        this.dataCache = null;
    }
    isDark(row, col) {
        if (row < 0 || this.moduleCount <= row || col < 0 || this.moduleCount <= col) {
            throw new Error(row + ',' + col);
        }
        return this.modules[row][col];
    }
    getModuleCount() {
        return this.moduleCount;
    }
    make() {
        // Calculate automatically typeNumber if provided is < 1
        if (this.typeNumber < 1) {
            let typeNumber = 1;
            for (typeNumber = 1; typeNumber < 40; typeNumber++) {
                const rsBlocks = QRRSBlock.getRSBlocks(typeNumber, this.errorCorrectLevel);
                const buffer = new QRBitBuffer();
                let totalDataCount = 0;
                for (let i = 0; i < rsBlocks.length; i++) {
                    totalDataCount += rsBlocks[i].dataCount;
                }
                for (let i = 0; i < this.dataList.length; i++) {
                    const data = this.dataList[i];
                    buffer.put(data.mode, 4);
                    buffer.put(data.getLength(), QRUtil.getLengthInBits(data.mode, typeNumber));
                    data.write(buffer);
                }
                if (buffer.getLengthInBits() <= totalDataCount * 8)
                    break;
            }
            this.typeNumber = typeNumber;
        }
        this.makeImpl(false, this.getBestMaskPattern());
    }
    makeImpl(test, maskPattern) {
        this.moduleCount = this.typeNumber * 4 + 17;
        this.modules = new Array(this.moduleCount);
        for (let row = 0; row < this.moduleCount; row++) {
            this.modules[row] = new Array(this.moduleCount);
            for (let col = 0; col < this.moduleCount; col++) {
                this.modules[row][col] = null; //(col + row) % 3;
            }
        }
        this.setupPositionProbePattern(0, 0);
        this.setupPositionProbePattern(this.moduleCount - 7, 0);
        this.setupPositionProbePattern(0, this.moduleCount - 7);
        this.setupPositionAdjustPattern();
        this.setupTimingPattern();
        this.setupTypeInfo(test, maskPattern);
        if (this.typeNumber >= 7) {
            this.setupTypeNumber(test);
        }
        if (this.dataCache == null) {
            this.dataCache = QRCode.createData(this.typeNumber, this.errorCorrectLevel, this.dataList);
        }
        this.mapData(this.dataCache, maskPattern);
    }
    setupPositionProbePattern(row, col) {
        for (let r = -1; r <= 7; r++) {
            if (row + r <= -1 || this.moduleCount <= row + r)
                continue;
            for (let c = -1; c <= 7; c++) {
                if (col + c <= -1 || this.moduleCount <= col + c)
                    continue;
                if ((0 <= r && r <= 6 && (c == 0 || c == 6)) ||
                    (0 <= c && c <= 6 && (r == 0 || r == 6)) ||
                    (2 <= r && r <= 4 && 2 <= c && c <= 4)) {
                    this.modules[row + r][col + c] = true;
                }
                else {
                    this.modules[row + r][col + c] = false;
                }
            }
        }
    }
    getBestMaskPattern() {
        let minLostPoint = 0;
        let pattern = 0;
        for (let i = 0; i < 8; i++) {
            this.makeImpl(true, i);
            const lostPoint = QRUtil.getLostPoint(this);
            if (i == 0 || minLostPoint > lostPoint) {
                minLostPoint = lostPoint;
                pattern = i;
            }
        }
        return pattern;
    }
    setupTimingPattern() {
        for (let r = 8; r < this.moduleCount - 8; r++) {
            if (this.modules[r][6] != null) {
                continue;
            }
            this.modules[r][6] = r % 2 == 0;
        }
        for (let c = 8; c < this.moduleCount - 8; c++) {
            if (this.modules[6][c] != null) {
                continue;
            }
            this.modules[6][c] = c % 2 == 0;
        }
    }
    setupPositionAdjustPattern() {
        const pos = QRUtil.getPatternPosition(this.typeNumber);
        for (let i = 0; i < pos.length; i++) {
            for (let j = 0; j < pos.length; j++) {
                const row = pos[i];
                const col = pos[j];
                if (this.modules[row][col] != null) {
                    continue;
                }
                for (let r = -2; r <= 2; r++) {
                    for (let c = -2; c <= 2; c++) {
                        if (r == -2 || r == 2 || c == -2 || c == 2 || (r == 0 && c == 0)) {
                            this.modules[row + r][col + c] = true;
                        }
                        else {
                            this.modules[row + r][col + c] = false;
                        }
                    }
                }
            }
        }
    }
    setupTypeNumber(test) {
        const bits = QRUtil.getBCHTypeNumber(this.typeNumber);
        for (let i = 0; i < 18; i++) {
            const mod = !test && ((bits >> i) & 1) == 1;
            this.modules[Math.floor(i / 3)][(i % 3) + this.moduleCount - 8 - 3] = mod;
        }
        for (let i = 0; i < 18; i++) {
            const mod = !test && ((bits >> i) & 1) == 1;
            this.modules[(i % 3) + this.moduleCount - 8 - 3][Math.floor(i / 3)] = mod;
        }
    }
    setupTypeInfo(test, maskPattern) {
        const data = (this.errorCorrectLevel << 3) | maskPattern;
        const bits = QRUtil.getBCHTypeInfo(data);
        // vertical
        for (let i = 0; i < 15; i++) {
            const mod = !test && ((bits >> i) & 1) == 1;
            if (i < 6) {
                this.modules[i][8] = mod;
            }
            else if (i < 8) {
                this.modules[i + 1][8] = mod;
            }
            else {
                this.modules[this.moduleCount - 15 + i][8] = mod;
            }
        }
        // horizontal
        for (let i = 0; i < 15; i++) {
            const mod = !test && ((bits >> i) & 1) == 1;
            if (i < 8) {
                this.modules[8][this.moduleCount - i - 1] = mod;
            }
            else if (i < 9) {
                this.modules[8][15 - i - 1 + 1] = mod;
            }
            else {
                this.modules[8][15 - i - 1] = mod;
            }
        }
        // fixed module
        this.modules[this.moduleCount - 8][8] = !test;
    }
    mapData(data, maskPattern) {
        let inc = -1;
        let row = this.moduleCount - 1;
        let bitIndex = 7;
        let byteIndex = 0;
        for (let col = this.moduleCount - 1; col > 0; col -= 2) {
            if (col == 6)
                col--;
            for (;;) {
                for (let c = 0; c < 2; c++) {
                    if (this.modules[row][col - c] == null) {
                        let dark = false;
                        if (byteIndex < data.length) {
                            dark = ((data[byteIndex] >>> bitIndex) & 1) == 1;
                        }
                        const mask = QRUtil.getMask(maskPattern, row, col - c);
                        if (mask) {
                            dark = !dark;
                        }
                        this.modules[row][col - c] = dark;
                        bitIndex--;
                        if (bitIndex == -1) {
                            byteIndex++;
                            bitIndex = 7;
                        }
                    }
                }
                row += inc;
                if (row < 0 || this.moduleCount <= row) {
                    row -= inc;
                    inc = -inc;
                    break;
                }
            }
        }
    }
    static createData(typeNumber, errorCorrectLevel, dataList) {
        const rsBlocks = QRRSBlock.getRSBlocks(typeNumber, errorCorrectLevel);
        const buffer = new QRBitBuffer();
        for (let i = 0; i < dataList.length; i++) {
            const data = dataList[i];
            buffer.put(data.mode, 4);
            buffer.put(data.getLength(), QRUtil.getLengthInBits(data.mode, typeNumber));
            data.write(buffer);
        }
        // calc num max data.
        let totalDataCount = 0;
        for (let i = 0; i < rsBlocks.length; i++) {
            totalDataCount += rsBlocks[i].dataCount;
        }
        if (buffer.getLengthInBits() > totalDataCount * 8) {
            throw new Error('code length overflow. (' +
                buffer.getLengthInBits() +
                '>' +
                totalDataCount * 8 +
                ')');
        }
        // end code
        if (buffer.getLengthInBits() + 4 <= totalDataCount * 8) {
            buffer.put(0, 4);
        }
        // padding
        while (buffer.getLengthInBits() % 8 != 0) {
            buffer.putBit(false);
        }
        // padding
        for (;;) {
            if (buffer.getLengthInBits() >= totalDataCount * 8) {
                break;
            }
            buffer.put(QRCode.PAD0, 8);
            if (buffer.getLengthInBits() >= totalDataCount * 8) {
                break;
            }
            buffer.put(QRCode.PAD1, 8);
        }
        return QRCode.createBytes(buffer, rsBlocks);
    }
    static createBytes(buffer, rsBlocks) {
        let offset = 0;
        let maxDcCount = 0;
        let maxEcCount = 0;
        const dcdata = new Array(rsBlocks.length);
        const ecdata = new Array(rsBlocks.length);
        for (let r = 0; r < rsBlocks.length; r++) {
            const dcCount = rsBlocks[r].dataCount;
            const ecCount = rsBlocks[r].totalCount - dcCount;
            maxDcCount = Math.max(maxDcCount, dcCount);
            maxEcCount = Math.max(maxEcCount, ecCount);
            dcdata[r] = new Array(dcCount);
            for (let i = 0; i < dcdata[r].length; i++) {
                dcdata[r][i] = 0xff & buffer.buffer[i + offset];
            }
            offset += dcCount;
            const rsPoly = QRUtil.getErrorCorrectPolynomial(ecCount);
            const rawPoly = new QRPolynomial(dcdata[r], rsPoly.getLength() - 1);
            const modPoly = rawPoly.mod(rsPoly);
            ecdata[r] = new Array(rsPoly.getLength() - 1);
            for (let i = 0; i < ecdata[r].length; i++) {
                const modIndex = i + modPoly.getLength() - ecdata[r].length;
                ecdata[r][i] = modIndex >= 0 ? modPoly.get(modIndex) : 0;
            }
        }
        let totalCodeCount = 0;
        for (let i = 0; i < rsBlocks.length; i++) {
            totalCodeCount += rsBlocks[i].totalCount;
        }
        const data = new Array(totalCodeCount);
        let index = 0;
        for (let i = 0; i < maxDcCount; i++) {
            for (let r = 0; r < rsBlocks.length; r++) {
                if (i < dcdata[r].length) {
                    data[index++] = dcdata[r][i];
                }
            }
        }
        for (let i = 0; i < maxEcCount; i++) {
            for (let r = 0; r < rsBlocks.length; r++) {
                if (i < ecdata[r].length) {
                    data[index++] = ecdata[r][i];
                }
            }
        }
        return data;
    }
}
QRCode.PAD0 = 0xec;
QRCode.PAD1 = 0x11;

/**
 * Generate QR SVG
 * @author Johan Nordberg <code@johan-nordberg.com>
 */
function generate(text, level = 'L', version = -1) {
    const qr = new QRCode(version, ErrorCorrectLevel[level]);
    const rects = [];
    qr.addData(text);
    qr.make();
    const rows = qr.modules;
    const size = rows.length;
    for (const [y, row] of rows.entries()) {
        let rect;
        for (const [x, on] of row.entries()) {
            if (on) {
                if (!rect)
                    rect = { x, y, width: 0, height: 1 };
                rect.width++;
            }
            else {
                if (rect && rect.width > 0) {
                    rects.push(rect);
                }
                rect = undefined;
            }
        }
        if (rect && rect.width > 0) {
            rects.push(rect);
        }
    }
    const svg = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}">`];
    for (const { x, y, width, height } of rects) {
        svg.push(`<rect x="${x}" y="${y}" width="${width}" height="${height}" />`);
    }
    svg.push('</svg>');
    return svg.join('');
}

var success$1 = "Success";
var scanOrButton$1 = "Scan the QR-code with APLink or use the button of Launch.";
var scan$1 = "Scan the QR-code with APLink.";
var login$1 = "Login";
var sign$1 = "Sign";
var submit$1 = "Submit";
var openAPLink$1 = "Please open APLink Wallet to review and sign the transaction.";
var review$1 = "Please review and sign the transaction in the linked wallet.";
var signManually$1 = "Sign manually or with another device";
var preparingRequest$1 = "Preparing request...";
var loading$1 = "Loading";
var downloadNow$1 = "Download now";
var haveAPLink$1 = "Don't have APLink yet? ";
var launchAPLink$1 = "Launch APLink";
var linkCopied$1 = "Link copied - Paste in APLink";
var copyRequestLink$1 = "Copy request link";
var proceed$1 = "proceed without the fee";
var accept = " or accept the fee shown below to pay for these costs.";
var offerExpires$1 = "Offer expires in {expires}";
var offerExpired$1 = "Offer expired";
var modalClosed$1 = "Modal closed";
var resourcesOffered$1 = "Resources offered by ";
var transactionFee$1 = "Transaction Fee";
var account$1 = "Your account lacks the network resources for this transaction and it cannot be covered for free.";
var acceptFee$1 = "Accept Fee of {fee}";
var manually$1 = "Sign manually";
var wantToSign$1 = "Want to sign with another device or didn’t get the signing request in your wallet, scan this QR or copy request and paste in app.";
var unableDeliver$1 = "Unable to deliver the request to “{deviceName}”.";
var unableDeliverLinkWallet$1 = "Unable to deliver the request to the linked wallet.";
var UnableReachDevice$1 = "Unable to reach device";
var loginCompleted$1 = "Login completed.";
var transactionSigned$1 = "Transaction signed.";
var transactionError$1 = "Transaction Error";
var en = {
	success: success$1,
	scanOrButton: scanOrButton$1,
	scan: scan$1,
	login: login$1,
	sign: sign$1,
	submit: submit$1,
	openAPLink: openAPLink$1,
	review: review$1,
	signManually: signManually$1,
	preparingRequest: preparingRequest$1,
	loading: loading$1,
	downloadNow: downloadNow$1,
	haveAPLink: haveAPLink$1,
	launchAPLink: launchAPLink$1,
	linkCopied: linkCopied$1,
	copyRequestLink: copyRequestLink$1,
	"youCanTryTo ": "You can try to ",
	proceed: proceed$1,
	accept: accept,
	offerExpires: offerExpires$1,
	offerExpired: offerExpired$1,
	modalClosed: modalClosed$1,
	resourcesOffered: resourcesOffered$1,
	transactionFee: transactionFee$1,
	account: account$1,
	acceptFee: acceptFee$1,
	manually: manually$1,
	wantToSign: wantToSign$1,
	unableDeliver: unableDeliver$1,
	unableDeliverLinkWallet: unableDeliverLinkWallet$1,
	UnableReachDevice: UnableReachDevice$1,
	loginCompleted: loginCompleted$1,
	transactionSigned: transactionSigned$1,
	transactionError: transactionError$1
};

var success = "Success";
var scanOrButton = "使用APLink钱包扫码或点击启用";
var scan = "使用APLink钱包扫描二维码";
var login = "登录";
var sign = "签名";
var submit = "提交";
var openAPLink = "请打开APLink钱包查看并签署交易。";
var review = "请在连接钱包中审核并签署交易。";
var signManually = "手动签名或使用其他设备签名";
var preparingRequest = "正在准备请求...";
var loading = "加载中";
var downloadNow = "立即下载";
var haveAPLink = "还没有APLink？";
var launchAPLink = "启动 APLink";
var linkCopied = "链接已复制 - 粘贴到 APLink";
var copyRequestLink = "复制链接";
var youCanTryTo = "你可以试试";
var proceed = "免费进行";
var accep = "或接受下面显示的费用来支付这些费用。";
var offerExpires = "优惠在 {expires} 后过期";
var offerExpired = "报价已过期";
var modalClosed = "模态关闭";
var resourcesOffered = "提供的资源";
var transactionFee = "交易费";
var account = "您的账户缺少本次交易的网络资源，无法免费覆盖。";
var acceptFee = "接受 {fee} 的费用";
var manually = "手动签名";
var wantToSign = "想用其他设备签名或钱包中没有收到签名请求，请扫描此二维码或复制请求并粘贴到应用程序中。";
var unableDeliver = "无法将请求传送到“{deviceName}”。";
var unableDeliverLinkWallet = "无法将请求传送到链接的钱包。";
var UnableReachDevice = "无法连接到设备";
var loginCompleted = "登录完成。";
var transactionSigned = "交易已签署。";
var transactionError = "交易错误";
var zh = {
	success: success,
	scanOrButton: scanOrButton,
	scan: scan,
	login: login,
	sign: sign,
	submit: submit,
	openAPLink: openAPLink,
	review: review,
	signManually: signManually,
	preparingRequest: preparingRequest,
	loading: loading,
	downloadNow: downloadNow,
	haveAPLink: haveAPLink,
	launchAPLink: launchAPLink,
	linkCopied: linkCopied,
	copyRequestLink: copyRequestLink,
	youCanTryTo: youCanTryTo,
	proceed: proceed,
	accep: accep,
	offerExpires: offerExpires,
	offerExpired: offerExpired,
	modalClosed: modalClosed,
	resourcesOffered: resourcesOffered,
	transactionFee: transactionFee,
	account: account,
	acceptFee: acceptFee,
	manually: manually,
	wantToSign: wantToSign,
	unableDeliver: unableDeliver,
	unableDeliverLinkWallet: unableDeliverLinkWallet,
	UnableReachDevice: UnableReachDevice,
	loginCompleted: loginCompleted,
	transactionSigned: transactionSigned,
	transactionError: transactionError
};

class Intl {
    constructor(currentLocale) {
        const locales = {
            'zh-cn': zh,
            'en-us': en,
        };
        this.locale = locales[currentLocale] || {};
    }
    get(key, variables) {
        const value = this.locale[key];
        if (value) {
            if (variables) {
                return value.replace(/\{(.*?)\}/, (_, $1) => variables[$1] || '');
            }
            else {
                return value;
            }
        }
        return '';
    }
}

async function apiCall(url, body) {
    return (await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
    })).json();
}
const version = 'fuel/2';
/** Return true if otherVersion is same or greater than current fuel version. */
function compareVersion(otherVersion) {
    return parseVersion(otherVersion) >= parseVersion(version);
}
function parseVersion(string) {
    const parts = string.trim().split('/');
    if (parts.length === 2 && parts[0] === 'fuel') {
        return parseInt(parts[1]) || 0;
    }
    return -1;
}
async function fuel(request, session, updatePrepareStatus, supportedChains, referrer) {
    assertEligible(request, session);
    updatePrepareStatus('Detecting if network resources are required.');
    const chainId = request.getChainId();
    const nodeUrl = supportedChains[String(chainId)];
    if (!nodeUrl) {
        throw new Error(`Blockchain not supported by this resource provider.`);
    }
    const result = await apiCall(nodeUrl + '/v1/resource_provider/request_transaction', {
        ref: referrer,
        request,
        signer: session.auth,
    });
    if (!result || !result.data) {
        throw new Error('Invalid response from resource provider.');
    }
    if (!result.data.signatures || !result.data.signatures[0]) {
        throw new Error('No signature returned from resource provider.');
    }
    if (result.code === 402 && !result.data.fee) {
        throw new Error('Resource provider returned a response indicating required payment, but provided no fee amount.');
    }
    // Clone the request for modification
    const cloned = request.clone();
    // Set the required fee onto the request for signature providers
    if (result.code === 402) {
        if (request.getInfoKey('no_fee')) {
            throw new Error('Fee required but sender opted out.');
        }
        cloned.setInfoKey('txfee', result.data.fee);
    }
    // If the fee costs exist, set them on the request for the signature provider to consume
    if (result.data.costs) {
        cloned.setInfoKey('txfeecpu', result.data.costs.cpu);
        cloned.setInfoKey('txfeenet', result.data.costs.net);
        cloned.setInfoKey('txfeeram', result.data.costs.ram);
    }
    // Set the cosigner signature onto the request for signature providers
    const signatures = result.data.signatures.map((s) => Signature.from(s));
    cloned.setInfoKey('cosig', signatures, { type: Signature, array: true });
    // Modify the request based on the response from the API
    cloned.data.req = (await SigningRequest.create({ transaction: { ...result.data.request[1] } }, { abiProvider: request.abiProvider })).data.req;
    return cloned;
}
function assertEligible(request, session) {
    if (request.getRawInfoKey('no_modify')) {
        throw new Error('Request cannot be modified.');
    }
    if (request.isIdentity()) {
        throw new Error('Identity requests cannot be co-signed.');
    }
    const firstAction = request.getRawActions()[0];
    if (!firstAction) {
        throw new Error('No actions in request.');
    }
    const firstAuthorizer = firstAction.authorization[0];
    if (!firstAction) {
        throw new Error('First authorization missing.');
    }
    if (!firstAuthorizer.actor.equals(session.auth.actor) &&
        !firstAuthorizer.actor.equals(PlaceholderName)) {
        throw new Error('Not first authorizer.');
    }
}

const AbortPrepare = Symbol();
const SkipFee = Symbol();
const SkipToManual = Symbol();
const defaultSupportedChains = {
    '2403d6f602a87977f898aa3c62c79a760f458745904a15b3cd63a106f62adc16': 'https://expnode.amaxscan.io',
    '208dacab3cd2e181c86841613cf05d9c60786c677e4ce86b266d0a58884968f7': 'https://test-chain.ambt.art',
};
class Storage {
    constructor(keyPrefix) {
        this.keyPrefix = keyPrefix;
    }
    async write(key, data) {
        localStorage.setItem(this.storageKey(key), data);
    }
    async read(key) {
        return localStorage.getItem(this.storageKey(key));
    }
    async remove(key) {
        localStorage.removeItem(this.storageKey(key));
    }
    storageKey(key) {
        return `${this.keyPrefix}-${key}`;
    }
}
class BrowserTransport {
    constructor(options = {}) {
        this.options = options;
        this.classPrefix = options.classPrefix || 'aplink-link';
        this.injectStyles = !(options.injectStyles === false);
        this.importantStyles = !(options.importantStyles === false);
        this.requestStatus = !(options.requestStatus === false);
        this.fuelEnabled = false; // options.disableGreymassFuel !== true
        this.fuelReferrer = options.fuelReferrer || 'teamgreymass';
        this.storage = new Storage(options.storagePrefix || 'aplink-link');
        this.supportedChains = options.supportedChains || defaultSupportedChains;
        this.showingManual = false;
        this.intl = new Intl(options.currentLocale || 'en-us');
    }
    closeModal() {
        this.hide();
        if (this.activeCancel) {
            this.activeRequest = undefined;
            this.activeCancel(this.intl.get('modalClosed'));
            this.activeCancel = undefined;
        }
    }
    setupElements() {
        this.showingManual = false;
        if (this.injectStyles && !this.styleEl) {
            this.styleEl = document.createElement('style');
            this.styleEl.type = 'text/css';
            let css = styleText.replace(/%prefix%/g, this.classPrefix);
            if (this.importantStyles) {
                css = css
                    .split('\n')
                    .map((line) => line.replace(/;$/i, ' !important;'))
                    .join('\n');
            }
            this.styleEl.appendChild(document.createTextNode(css));
            document.head.appendChild(this.styleEl);
        }
        if (!this.containerEl) {
            this.containerEl = this.createEl();
            this.containerEl.className = this.classPrefix;
            this.containerEl.onclick = (event) => {
                if (event.target === this.containerEl) {
                    event.stopPropagation();
                    this.closeModal();
                }
            };
            document.body.appendChild(this.containerEl);
        }
        if (!this.requestEl) {
            const wrapper = this.createEl({ class: 'inner' });
            const closeButton = this.createEl({ class: 'close' });
            closeButton.onclick = (event) => {
                event.stopPropagation();
                this.closeModal();
            };
            this.requestEl = this.createEl({ class: 'request' });
            wrapper.appendChild(this.requestEl);
            wrapper.appendChild(closeButton);
            const version = this.createEl({
                class: 'version',
                text: `${BrowserTransport.version} (${Link.version})`,
            });
            version.onclick = (event) => {
                event.stopPropagation();
                window.open('https://github.com/armoniax/amaxjs/tree/main/packages/anchor-link', '_blank');
            };
            wrapper.appendChild(version);
            this.containerEl.appendChild(wrapper);
        }
    }
    createEl(attrs) {
        if (!attrs)
            attrs = {};
        const el = document.createElement(attrs.tag || 'div');
        for (const attr of Object.keys(attrs)) {
            const value = attrs[attr];
            switch (attr) {
                case 'src':
                    el.setAttribute(attr, value);
                    break;
                case 'tag':
                    break;
                case 'content':
                    if (typeof value === 'string') {
                        el.appendChild(document.createTextNode(value));
                    }
                    else {
                        el.appendChild(value);
                    }
                    break;
                case 'text':
                    el.appendChild(document.createTextNode(value));
                    break;
                case 'class':
                    el.className = `${this.classPrefix}-${value}`;
                    break;
                default:
                    el.setAttribute(attr, value);
            }
        }
        return el;
    }
    hide() {
        if (this.containerEl) {
            this.containerEl.classList.remove(`${this.classPrefix}-active`);
        }
        this.clearTimers();
    }
    show() {
        if (this.containerEl) {
            this.containerEl.classList.add(`${this.classPrefix}-active`);
        }
    }
    showDialog(args) {
        this.setupElements();
        const infoEl = this.createEl({ class: 'info' });
        const infoSubtitle = this.createEl({
            class: 'subtitle',
            tag: 'span',
            content: args.subtitle,
        });
        if (args.type !== 'login') {
            const infoTitle = this.createEl({ class: 'title', tag: 'span', content: args.title });
            infoEl.appendChild(infoTitle);
        }
        infoEl.appendChild(infoSubtitle);
        const logoEl = this.createEl({ class: 'logo' });
        if (args.type) {
            logoEl.classList.add(args.type);
            this.requestEl.classList.add(args.type);
        }
        emptyElement(this.requestEl);
        this.requestEl.appendChild(logoEl);
        this.requestEl.appendChild(infoEl);
        if (args.content) {
            this.requestEl.appendChild(args.content);
        }
        if (args.action) {
            const buttonEl = this.createEl({ tag: 'a', class: 'button', text: args.action.text });
            buttonEl.addEventListener('click', (event) => {
                event.preventDefault();
                args.action.callback();
            });
            this.requestEl.appendChild(buttonEl);
        }
        if (args.footnote) {
            const footnoteEl = this.createEl({ class: 'footnote', content: args.footnote });
            this.requestEl.appendChild(footnoteEl);
        }
        this.show();
    }
    async displayRequest(request, title, subtitle, showFooter = true) {
        const sameDeviceRequest = request.clone();
        const returnUrl = generateReturnUrl();
        sameDeviceRequest.setInfoKey('same_device', true);
        sameDeviceRequest.setInfoKey('return_path', returnUrl);
        // const sameDeviceUri = sameDeviceRequest.encode(true, false)
        const crossDeviceUri = request.encode(true, false);
        const qrEl = this.createEl({ class: 'qr' });
        try {
            qrEl.innerHTML = generate(crossDeviceUri);
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Unable to generate QR code', error);
        }
        const copyEl = this.createEl({ class: 'copy' });
        const copyA = this.createEl({ tag: 'a', text: this.intl.get('copyRequestLink') }); // 'Copy request link'
        const copySpan = this.createEl({ tag: 'span', text: this.intl.get('linkCopied') }); // 'Link copied - Paste in APLink'
        copyEl.appendChild(copyA);
        copyEl.appendChild(copySpan);
        // qrEl.appendChild(copyEl)
        copyA.addEventListener('click', (event) => {
            event.preventDefault();
            copyToClipboard(crossDeviceUri);
            copyEl.classList.add('copied');
            setTimeout(() => {
                copyEl.classList.remove('copied');
            }, 2000);
        });
        const svg = qrEl.querySelector('svg');
        if (svg) {
            svg.addEventListener('click', (event) => {
                event.preventDefault();
                qrEl.classList.toggle('zoom');
            });
        }
        const linkEl = this.createEl({ class: 'download', text: this.intl.get('haveAPLink') });
        const linkA = this.createEl({
            tag: 'a',
            class: 'button',
            href: crossDeviceUri,
            text: this.intl.get('launchAPLink'), // 'Launch APLink',
        });
        const footnoteLink = this.createEl({
            tag: 'a',
            target: '_blank',
            href: 'http://www.aplink.app/home',
            text: this.intl.get('downloadNow'), // 'Download now',
        });
        linkEl.appendChild(footnoteLink);
        // if (isFirefox() || isBrave()) {
        //     // this prevents firefox/brave from killing the websocket connection once the link is clicked
        //     const iframe = this.createEl({
        //         class: 'wskeepalive',
        //         src: 'about:blank',
        //         tag: 'iframe',
        //     })
        //     linkEl.appendChild(iframe)
        //     linkA.addEventListener('click', (event) => {
        //         event.preventDefault()
        //         iframe.setAttribute('src', sameDeviceUri)
        //     })
        // } else {
        //     linkA.addEventListener('click', (event) => {
        //         event.preventDefault()
        //         window.location.href = sameDeviceUri
        //     })
        // }
        const content = this.createEl({ class: 'info' });
        content.appendChild(qrEl);
        content.appendChild(copyEl);
        content.appendChild(linkEl.cloneNode(true));
        content.appendChild(linkA);
        let footnote;
        if (showFooter) {
            footnote = linkEl;
        }
        this.showDialog({
            title,
            subtitle,
            footnote,
            content,
            type: 'login',
        });
    }
    async showLoading() {
        const status = this.createEl({
            tag: 'span',
            text: this.intl.get('preparingRequest'), // 'Preparing request...',
        });
        this.prepareStatusEl = status;
        this.showDialog({
            title: this.intl.get('loading'),
            subtitle: status,
            type: 'loading',
        });
    }
    // login
    onRequest(request, cancel) {
        this.clearTimers();
        this.activeRequest = request;
        this.activeCancel = cancel;
        const title = this.intl.get(request.isIdentity() ? 'login' : 'sign');
        const subtitle = this.intl.get('scan');
        this.displayRequest(request, title, subtitle).catch(cancel);
    }
    onSessionRequest(session, request, cancel) {
        if (session.metadata.sameDevice) {
            request.setInfoKey('return_path', generateReturnUrl());
        }
        if (session.type === 'fallback') {
            this.onRequest(request, cancel);
            if (session.metadata.sameDevice) {
                // trigger directly on a fallback same-device session
                window.location.href = request.encode();
            }
            return;
        }
        this.clearTimers();
        this.activeRequest = request;
        this.activeCancel = cancel;
        const timeout = session.metadata.timeout || 60 * 1000 * 2;
        const deviceName = session.metadata.name;
        let subtitle;
        if (deviceName && deviceName.length > 0) {
            // `Please open APLink Wallet on “${deviceName}” to review and sign the transaction.`
            subtitle = this.intl.get('openAPLink');
        }
        else {
            // 'Please review and sign the transaction in the linked wallet.'
            subtitle = this.intl.get('review');
        }
        const title = this.createEl({ tag: 'span', text: this.intl.get('sign') });
        const expires = new Date(Date.now() + timeout);
        const updateCountdown = () => {
            title.textContent = `${this.intl.get('sign')} & ${this.intl.get('submit')} - ${countdownFormat(expires)}`;
        };
        this.countdownTimer = setInterval(updateCountdown, 200);
        updateCountdown();
        const content = this.createEl({ class: 'info' });
        // const manualHr = this.createEl({tag: 'hr'})
        // const manualA = this.createEl({
        //     tag: 'a',
        //     text: this.intl.get('signManually'), // 'Sign manually or with another device',
        //     class: 'manual',
        // })
        // manualA.addEventListener('click', (event) => {
        //     event.preventDefault()
        //     const error = new SessionError('Manual', 'E_TIMEOUT', session)
        //     error[SkipToManual] = true
        //     cancel(error)
        // })
        // content.appendChild(manualHr)
        // content.appendChild(manualA)
        this.showDialog({
            title,
            subtitle,
            content,
        });
        if (session.metadata.sameDevice) {
            if (session.metadata.launchUrl) {
                window.location.href = session.metadata.launchUrl;
            }
            else if (isAppleHandheld()) {
                window.location.href = 'anchor://link';
            }
        }
    }
    sendSessionPayload(payload, session) {
        if (!session.metadata.triggerUrl || !session.metadata.sameDevice) {
            // not same device or no trigger url supported
            return false;
        }
        if (payload.array.length > 700) {
            // url could be clipped by iOS
            return false;
        }
        window.location.href = session.metadata.triggerUrl.replace('%s', Base64u.encode(payload.array));
        return true;
    }
    clearTimers() {
        if (this.closeTimer) {
            clearTimeout(this.closeTimer);
            this.closeTimer = undefined;
        }
        if (this.countdownTimer) {
            clearTimeout(this.countdownTimer);
            this.countdownTimer = undefined;
        }
    }
    async showFee(request, fee) {
        this.activeRequest = request;
        const cancelPromise = new Promise((resolve, reject) => {
            this.activeCancel = (reason) => {
                let error;
                if (typeof reason === 'string') {
                    error = new Error(reason);
                }
                else {
                    error = reason;
                }
                error[AbortPrepare] = true;
                reject(error);
            };
        });
        const content = this.createEl({ class: 'info' });
        const feePart1 = this.createEl({
            tag: 'span',
            text: this.intl.get('youCanTryTo'), // 'You can try to ',
        });
        const feeBypass = this.createEl({
            tag: 'a',
            text: this.intl.get('proceed'), // 'proceed without the fee',
        });
        const feePart2 = this.createEl({
            tag: 'span',
            text: this.intl.get('accept'), // ' or accept the fee shown below to pay for these costs.',
        });
        const feeDescription = this.createEl({
            class: 'subtitle',
            tag: 'span',
        });
        feeDescription.appendChild(feePart1);
        feeDescription.appendChild(feeBypass);
        feeDescription.appendChild(feePart2);
        content.appendChild(feeDescription);
        const expireEl = this.createEl({
            tag: 'span',
            class: 'subtitle',
            text: this.intl.get('offerExpires', { expires: '--:--' }), // 'Offer expires in --:--',
        });
        content.appendChild(expireEl);
        const expires = request.getRawTransaction().expiration.toDate();
        const expireTimer = setInterval(() => {
            expireEl.textContent = this.intl.get('offerExpires', {
                expires: countdownFormat(expires),
            }); // `Offer expires in ${countdownFormat(expires)}`
            if (expires.getTime() < Date.now()) {
                this.activeCancel(this.intl.get('offerExpired'));
            }
        }, 200);
        const footnote = this.createEl({
            tag: 'span',
            text: this.intl.get('resourcesOffered'), // 'Resources offered by ',
        });
        const footnoteLink = this.createEl({
            tag: 'a',
            target: '_blank',
            href: 'https://greymass.com/en/fuel',
            text: 'Greymass Fuel',
        });
        footnote.appendChild(footnoteLink);
        const skipPromise = waitForEvent(feeBypass, 'click').then(() => {
            const error = new Error('Skipped fee');
            error[SkipFee] = true;
            throw error;
        });
        const confirmPromise = new Promise((resolve) => {
            this.showDialog({
                title: this.intl.get('transactionFee'),
                // 'Your account lacks the network resources for this transaction and it cannot be covered for free.',
                subtitle: this.intl.get('account'),
                type: 'fuel',
                content,
                action: {
                    text: this.intl.get('acceptFee', { fee }),
                    callback: resolve,
                },
                footnote,
            });
        });
        await Promise.race([confirmPromise, skipPromise, cancelPromise]).finally(() => {
            clearInterval(expireTimer);
        });
    }
    showRecovery(request, session) {
        request.data.info = request.data.info.filter((pair) => pair.key !== 'return_path');
        if (session.type === 'channel') {
            const channelSession = session;
            if (channelSession.addLinkInfo) {
                channelSession.addLinkInfo(request);
            }
        }
        this.displayRequest(request, this.intl.get('manually'), // 'Sign manually',
        this.intl.get('wantToSign'), // 'Want to sign with another device or didn’t get the signing request in your wallet, scan this QR or copy request and paste in app.',
        false);
        this.showingManual = true;
    }
    async prepare(request, session) {
        this.showLoading();
        if (!this.fuelEnabled || !session || request.isIdentity()) {
            // don't attempt to cosign id request or if we don't have a session attached
            return request;
        }
        if (typeof session.metadata.cosignerVersion === 'string' &&
            compareVersion(session.metadata.cosignerVersion)) {
            // if signer has cosigner, only attempt to cosign here if we have a newer version
            return request;
        }
        try {
            const result = fuel(request, session, (message) => {
                if (this.prepareStatusEl) {
                    this.prepareStatusEl.textContent = message;
                }
            }, this.supportedChains, this.fuelReferrer);
            const timeout = new Promise((r) => setTimeout(r, 5000)).then(() => {
                throw new Error('API timeout after 5000ms');
            });
            const modified = await Promise.race([result, timeout]);
            const fee = modified.getInfoKey('txfee');
            if (fee) {
                await this.showFee(modified, String(fee));
            }
            return modified;
        }
        catch (error) {
            if (error[AbortPrepare]) {
                this.hide();
                throw error;
            }
            else {
                // eslint-disable-next-line no-console
                console.info(`Skipping resource provider: ${error.message || error}`);
                if (error[SkipFee]) {
                    const modified = request.clone();
                    modified.setInfoKey('no_fee', true, 'bool');
                    return modified;
                }
            }
        }
        return request;
    }
    recoverError(error, request) {
        if (request === this.activeRequest &&
            (error['code'] === 'E_DELIVERY' || error['code'] === 'E_TIMEOUT') &&
            error['session']) {
            // recover from session errors by displaying a manual sign dialog
            if (this.showingManual) {
                // already showing recovery sign
                return true;
            }
            const session = error['session'];
            if (error[SkipToManual]) {
                this.showRecovery(request, session);
                return true;
            }
            const deviceName = session.metadata.name;
            const subtitle = this.createEl({
                tag: 'div',
                content: deviceName && deviceName.length > 0
                    ? this.intl.get('unableDeliver', { deviceName })
                    : this.intl.get('unableDeliverLinkWallet'),
            });
            const errorMsg = this.createEl({
                tag: 'div',
                text: error.message,
                class: 'error',
            });
            subtitle.appendChild(errorMsg);
            this.showDialog({
                title: this.intl.get('UnableReachDevice'),
                subtitle,
                type: 'warning',
                action: {
                    text: this.intl.get('manually'),
                    callback: () => {
                        this.showRecovery(request, session);
                    },
                },
            });
            return true;
        }
        return false;
    }
    onSuccess(request) {
        if (request === this.activeRequest) {
            this.clearTimers();
            if (this.requestStatus) {
                this.showDialog({
                    title: this.intl.get('success'),
                    subtitle: this.intl.get(request.isIdentity() ? 'loginCompleted' : 'transactionSigned'),
                    type: 'success',
                });
                this.closeTimer = setTimeout(() => {
                    this.hide();
                }, 1.5 * 1000);
            }
            else {
                this.hide();
            }
        }
    }
    onFailure(request, error) {
        if (request === this.activeRequest && error['code'] !== 'E_CANCEL') {
            this.clearTimers();
            if (this.requestStatus) {
                let errorMessage;
                if (isInstanceOf(error, APIError)) {
                    if (error.name === 'eosio_assert_message_exception') {
                        errorMessage = error.details[0].message;
                    }
                    else if (error.details.length > 0) {
                        errorMessage = error.details.map((d) => d.message).join('\n');
                    }
                    else {
                        errorMessage = error.message;
                    }
                }
                else {
                    errorMessage = error.message || String(error);
                }
                this.showDialog({
                    title: this.intl.get('transactionError'),
                    subtitle: this.createEl({
                        tag: 'div',
                        text: errorMessage,
                        class: 'error',
                    }),
                    type: 'error',
                });
            }
            else {
                this.hide();
            }
        }
        else {
            this.hide();
        }
    }
    userAgent() {
        return `BrowserTransport/${BrowserTransport.version} ${navigator.userAgent}`;
    }
}
/** Package version. */
BrowserTransport.version = '3.6.4'; // replaced by build script
function waitForEvent(element, eventName, timeout) {
    return new Promise((resolve, reject) => {
        const listener = (event) => {
            element.removeEventListener(eventName, listener);
            resolve(event);
        };
        element.addEventListener(eventName, listener);
        if (timeout) {
            setTimeout(() => {
                element.removeEventListener(eventName, listener);
                reject(new Error(`Timed out waiting for ${eventName}`));
            }, timeout);
        }
    });
}
function countdownFormat(date) {
    const timeLeft = date.getTime() - Date.now();
    if (timeLeft > 0) {
        return new Date(timeLeft).toISOString().substr(14, 5);
    }
    return '00:00';
}
function emptyElement(el) {
    while (el.firstChild) {
        el.removeChild(el.firstChild);
    }
}
/** Generate a return url that Anchor will redirect back to w/o reload. */
function generateReturnUrl() {
    if (isChromeiOS()) {
        // google chrome on iOS will always open new tab so we just ask it to open again as a workaround
        return 'googlechrome://';
    }
    if (isFirefoxiOS()) {
        // same for firefox
        return 'firefox:://';
    }
    if (isAppleHandheld() && isBrave()) {
        // and brave ios
        return 'brave://';
    }
    if (isAppleHandheld()) {
        // return url with unique fragment required for iOS safari to trigger the return url
        const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let rv = window.location.href.split('#')[0] + '#';
        for (let i = 0; i < 8; i++) {
            rv += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
        }
        return rv;
    }
    if (isAndroid() && isFirefox()) {
        return 'android-intent://org.mozilla.firefox';
    }
    if (isAndroid() && isEdge()) {
        return 'android-intent://com.microsoft.emmx';
    }
    if (isAndroid() && isOpera()) {
        return 'android-intent://com.opera.browser';
    }
    if (isAndroid() && isBrave()) {
        return 'android-intent://com.brave.browser';
    }
    if (isAndroid() && isAndroidWebView()) {
        return 'android-intent://webview';
    }
    if (isAndroid() && isChromeMobile()) {
        return 'android-intent://com.android.chrome';
    }
    return window.location.href;
}
function isAppleHandheld() {
    return /iP(ad|od|hone)/i.test(navigator.userAgent);
}
function isChromeiOS() {
    return /CriOS/.test(navigator.userAgent);
}
function isChromeMobile() {
    return /Chrome\/[.0-9]* Mobile/i.test(navigator.userAgent);
}
function isFirefox() {
    return /Firefox/i.test(navigator.userAgent);
}
function isFirefoxiOS() {
    return /FxiOS/.test(navigator.userAgent);
}
function isOpera() {
    return /OPR/.test(navigator.userAgent) || /Opera/.test(navigator.userAgent);
}
function isEdge() {
    return /Edg/.test(navigator.userAgent);
}
function isBrave() {
    return navigator['brave'] && typeof navigator['brave'].isBrave === 'function';
}
function isAndroid() {
    return /Android/.test(navigator.userAgent);
}
function isAndroidWebView() {
    return /wv/.test(navigator.userAgent) || /Android.*AppleWebKit/.test(navigator.userAgent);
}
function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
    }
    else {
        const el = document.createElement('textarea');
        try {
            el.setAttribute('contentEditable', '');
            el.value = text;
            document.body.appendChild(el);
            el.select();
            const range = document.createRange();
            range.selectNodeContents(el);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
            el.setSelectionRange(0, el.value.length);
            document.execCommand('copy');
        }
        finally {
            document.body.removeChild(el);
        }
    }
}

export { BrowserTransport as default };
//# sourceMappingURL=anchor-link-browser-transport.m.js.map
